import { Box, FormControlLabel } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import { ChangeEvent, useState } from 'react'
import { useNotify, useRedirect, useRefresh } from 'react-admin'
import { Card, Grid, TextLeft } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { Handler } from 'shared/helpers/typeHelper'
import { bgGray, cyan } from 'shared/theme/colors'
import styled, { css } from 'styled-components'

import { useUser } from '../../helpers/useUser'
import { cancelOrder, completeOrder, createOrder, Order, OrderStatus, useOrders } from '../../models/order'
import {
  Feature,
  FeatureId,
  FeatureNames,
  ProductSubscriptionBundleName,
  ProductSubscriptionBundleNames,
} from '../../models/subscription'

export function TeamUpsell({
  team,
  canAddOrderWithOverrides,
}: {
  team: any
  canAddOrderWithOverrides: boolean
}) {
  const { orders, refetch: refetchOrders } = useOrders({ teamId: team.id })
  const firstAdminId = team?.members?.find((m: any) => m.is_team_admin)?.id
  const { user: firstAdmin } = useUser(firstAdminId)
  const canCreateFlexStarterUpsell = firstAdmin?.app_permissions?.create_starter_upsell?.enabled || false
  const canCreateFactoringUpsell = firstAdmin?.app_permissions?.create_factoring_upsell?.enabled || false
  const isPremium = team.product_subscription.bundle_name === ProductSubscriptionBundleName.PREMIUM

  if (!firstAdmin) return null
  if (isPremium) return null
  if (!orders) return <InlineSpinner mt={5} mb={5} />

  const flexStarterOrder = getOrder(orders, Feature.FLEX_STARTER_SEAT)
  const factoringOrder = getOrder(orders, Feature.FACTORING_OR_INSTANT_PAY)

  return (
    <CardsWrap>
      <StyledCard hasSubtleShadow $isSelected>
        <Text variant="label1">
          {
            ProductSubscriptionBundleNames[
              team.product_subscription.bundle_name as ProductSubscriptionBundleName
            ]
          }
        </Text>
        <Entitlement
          order={flexStarterOrder}
          featureId={FeatureId.FLEX_STARTER_SEAT}
          featureName={Feature.FLEX_STARTER_SEAT}
          canAdd={canCreateFlexStarterUpsell}
          team={team}
          refetchOrders={refetchOrders}
          canAddOrderWithOverrides={canAddOrderWithOverrides}
        />
        <Box mt={2} />
        <Entitlement
          order={factoringOrder}
          featureId={FeatureId.FACTORING}
          featureName={Feature.FACTORING_OR_INSTANT_PAY}
          canAdd={canCreateFactoringUpsell}
          team={team}
          refetchOrders={refetchOrders}
          canAddOrderWithOverrides={canAddOrderWithOverrides}
        />
      </StyledCard>
    </CardsWrap>
  )
}

function Entitlement({
  featureId,
  featureName,
  canAdd,
  order,
  team,
  refetchOrders,
  canAddOrderWithOverrides,
}: {
  featureId: FeatureId
  featureName: Feature
  canAdd: boolean
  canAddOrderWithOverrides: boolean
  order?: Order
  team: any
  refetchOrders: Handler
}) {
  const refresh = useRefresh()
  const notify = useNotify()
  const [isCreateLoading, setIsCreateLoading] = useState(false)
  const [isCancelLoading, setIsCancelLoading] = useState(false)
  const [isCompleteLoading, setIsCompleteLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [immediate, setImmediate] = useState(false)
  const [bypassLineItems, setBypassLineItems] = useState(true)
  const [quantity, setQuantity] = useState('1')
  const name = FeatureNames[featureId]
  const redirectTo = useRedirect()

  function handleChange(e: ChangeEvent<HTMLInputElement>, isChecked: boolean) {
    e.preventDefault()
    if (isChecked) setIsCreating(true)
  }

  async function handleCreate() {
    setIsCreateLoading(true)
    try {
      await createOrder({
        featureId: featureId,
        name: featureName,
        quantity: quantity,
        immediate: immediate,
        bypass_line_items: bypassLineItems,
        team_id: team.id,
      })
      await refresh()
      await refetchOrders()
      redirectTo(`/teams/${team.id}/show`)
      notify('Order created', { type: 'success' })
    } catch (err: any) {
      const message = err?.response?.data ? JSON.stringify(err?.response?.data) : 'Something went wrong'
      notify(message, { type: 'error' })
    } finally {
      setIsCreateLoading(false)
      setIsCreating(false)
    }
  }

  async function handleCancel() {
    if (!order) return
    setIsCancelLoading(true)
    try {
      await cancelOrder(order.id)
      await refresh()
      await refetchOrders()
      redirectTo(`/teams/${team.id}/show`)
      notify('Order canceled', { type: 'success' })
    } catch (err: any) {
      const message = err?.response?.data ? JSON.stringify(err?.response?.data) : 'Something went wrong'
      notify(message, { type: 'error' })
    } finally {
      setIsCancelLoading(false)
    }
  }

  async function handleComplete() {
    if (!order) return
    setIsCompleteLoading(true)
    try {
      await completeOrder(order.id)
      await refresh()
      await refetchOrders()
      redirectTo(`/teams/${team.id}/show`)
      notify('Order completed', { type: 'success' })
    } catch (err: any) {
      const message = err?.response?.data ? JSON.stringify(err?.response?.data) : 'Something went wrong'
      notify(message, { type: 'error' })
    } finally {
      setIsCompleteLoading(false)
    }
  }

  const isChecked =
    (order?.status && [OrderStatus.EXECUTING, OrderStatus.COMPLETED].includes(order.status)) || false

  return (
    <div>
      <FormControlLabel
        control={<Checkbox checked={isChecked} onChange={handleChange} disabled={isChecked || !canAdd} />}
        label={
          <>
            {name} {order ? <>({order?.status})</> : null}
          </>
        }
      />

      {order?.status === OrderStatus.EXECUTING ? (
        <Grid columns="auto auto" gap="10px" mt={1}>
          {order?.can_complete ? (
            <Button size="medium" onClick={handleComplete} isLoading={isCompleteLoading}>
              Complete
            </Button>
          ) : null}
          {order?.can_cancel ? (
            <Button size="medium" onClick={handleCancel} isLoading={isCancelLoading}>
              Cancel
            </Button>
          ) : null}
        </Grid>
      ) : null}

      <SimpleDialog isOpen={isCreating} onClose={() => setIsCreating(false)}>
        <Text variant="header2" align="left">
          Are you sure to create {name} upsell?
        </Text>

        {canAddOrderWithOverrides && (
          <>
            <Box mt={4} />
            <TextLeft>
              <FormControlLabel
                control={<Checkbox checked={immediate} onChange={(_, checked) => setImmediate(checked)} />}
                label="Immediate"
              />

              <Box mt={0.2} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bypassLineItems}
                    onChange={(_, checked) => setBypassLineItems(checked)}
                  />
                }
                label="Bypass Line Items"
              />

              <Box mt={2} />
              <TextInput
                backgroundColor={bgGray}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                label="Quantity"
              />
            </TextLeft>
          </>
        )}

        <Box m={5} />
        <Button onClick={handleCreate} size="large" margin="0 auto" isLoading={isCreateLoading}>
          Submit
        </Button>
        <Box m={3} />
        <Button margin="0 auto" variant="text" onClick={() => setIsCreating(false)}>
          Cancel
        </Button>
      </SimpleDialog>
    </div>
  )
}

function getOrder(orders: Order[], feature: Feature) {
  return [...orders].reverse().find((o) => o.added_entitlements.find((e) => e.name === feature))
}

const CardsWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 700px;
  margin-top: 20px;
`

const StyledCard = styled(Card)<{
  $isSelected?: boolean
}>`
  text-align: left;
  border: 1px solid transparent;

  ${(p) =>
    p.$isSelected &&
    css`
      border-color: ${cyan};
    `}
`
